import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2, TrendingUp, Send, XCircle, Ban, CheckCircle2, UserX, Clock, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { subDays, subHours, format } from 'date-fns';
// DTC-K: route reminder-analytics N-day + 24h window anchors through
// the canonical clock so analytics queries are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { Badge } from '@/components/ui/badge';

const COLORS = {
  sent: '#10b981', // green
  failed: '#ef4444', // red
  suppressed: '#f97316', // orange
  confirmed: '#3b82f6', // blue
  canceled: '#8b5cf6', // purple
};

export const ReminderAnalytics = () => {
  const { activeShop } = useAuth();
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [channelFilter, setChannelFilter] = useState<'all' | 'email' | 'sms'>('all');

  // PHASE 5: Helper component for health stat cards
  const HealthStatCard = ({ label, value, icon: Icon, color }: any) => (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center space-x-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <span className="text-2xl font-bold">{value ?? 0}</span>
    </div>
  );

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['reminder-analytics', activeShop?.id, dateRange, channelFilter],
    queryFn: async () => {
      if (!activeShop?.id) return null;

      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const startDate = subDays(clock.now(), days);

      let query = supabase
        .from('reminder_analytics')
        .select('*')
        .eq('shop_id', activeShop.id)
        .gte('date', format(startDate, 'yyyy-MM-dd'))
        .order('date', { ascending: true });

      if (channelFilter !== 'all') {
        query = query.eq('channel', channelFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data;
    },
    enabled: !!activeShop?.id,
  });

  // PHASE 5: 24-hour health check
  const { data: recentHealth, isLoading: isHealthLoading } = useQuery({
    queryKey: ['reminder-health', activeShop?.id],
    queryFn: async () => {
      if (!activeShop?.id) return null;
      
      const last24h = subHours(clock.now(), 24);
      
      // Count queued reminders
      const { count: queuedCount } = await supabase
        .from('reminder_queue')
        .select('id', { count: 'exact', head: true })
        .eq('shop_id', activeShop.id)
        .eq('status', 'queued');
      
      // Get recent logs
      const { data: recentLogs } = await supabase
        .from('reminder_log')
        .select('status')
        .eq('shop_id', activeShop.id)
        .gte('created_at', last24h.toISOString());
      
      const sent = recentLogs?.filter(l => l.status === 'sent').length || 0;
      const failed = recentLogs?.filter(l => l.status === 'failed').length || 0;
      const suppressed = recentLogs?.filter(l => l.status === 'suppressed').length || 0;
      
      return { 
        queued: queuedCount || 0, 
        sent, 
        failed, 
        suppressed 
      };
    },
    enabled: !!activeShop?.id,
    refetchInterval: 60000 // Refresh every minute
  });

  if (isLoading) {

  // Helper component for health stat cards
  const HealthStatCard = ({ label, value, icon: Icon, color }: any) => (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center space-x-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <span className="text-2xl font-bold">{value ?? 0}</span>
    </div>
  );

  return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  // Aggregate totals
  const totals = analytics?.reduce(
    (acc, curr) => ({
      queued: acc.queued + curr.total_queued,
      sent: acc.sent + curr.total_sent,
      failed: acc.failed + curr.total_failed,
      suppressed: acc.suppressed + curr.total_suppressed,
      canceled: acc.canceled + curr.total_canceled,
      confirmed: acc.confirmed + curr.total_confirmed,
      customerCanceled: acc.customerCanceled + curr.total_customer_canceled,
      optOuts: acc.optOuts + curr.total_opt_outs,
    }),
    {
      queued: 0,
      sent: 0,
      failed: 0,
      suppressed: 0,
      canceled: 0,
      confirmed: 0,
      customerCanceled: 0,
      optOuts: 0,
    }
  );

  // Calculate rates
  const deliveryRate = totals && totals.queued > 0 
    ? ((totals.sent / totals.queued) * 100).toFixed(1)
    : '0.0';

  const confirmationRate = totals && totals.sent > 0 && channelFilter !== 'email'
    ? ((totals.confirmed / totals.sent) * 100).toFixed(1)
    : '0.0';

  // Prepare chart data
  const statusDistribution = totals ? [
    { name: 'Sent', value: totals.sent, color: COLORS.sent },
    { name: 'Failed', value: totals.failed, color: COLORS.failed },
    { name: 'Suppressed', value: totals.suppressed, color: COLORS.suppressed },
  ].filter(d => d.value > 0) : [];

  const channelBreakdown = analytics
    ? analytics.reduce((acc, curr) => {
        const existing = acc.find(item => item.channel === curr.channel);
        if (existing) {
          existing.sent += curr.total_sent;
          existing.failed += curr.total_failed;
        } else {
          acc.push({
            channel: curr.channel.toUpperCase(),
            sent: curr.total_sent,
            failed: curr.total_failed,
          });
        }
        return acc;
      }, [] as Array<{ channel: string; sent: number; failed: number }>)
    : [];

  return (
    <div className="space-y-6">
      {/* Filters */}

      {/* PHASE 5: 24-Hour Health Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Reminder Health (Last 24 Hours)
          </CardTitle>
          <CardDescription>Real-time system status and activity</CardDescription>
        </CardHeader>
        <CardContent>
          {isHealthLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <HealthStatCard 
                label="Queued" 
                value={recentHealth?.queued} 
                icon={Clock} 
                color="text-blue-500"
              />
              <HealthStatCard 
                label="Sent" 
                value={recentHealth?.sent} 
                icon={CheckCircle2} 
                color="text-green-500"
              />
              <HealthStatCard 
                label="Failed" 
                value={recentHealth?.failed} 
                icon={XCircle} 
                color="text-red-500"
              />
              <HealthStatCard 
                label="Suppressed" 
                value={recentHealth?.suppressed} 
                icon={Ban} 
                color="text-orange-500"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historical Analytics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Reminder Analytics
          </CardTitle>
          <CardDescription>
            Track delivery rates, customer engagement, and performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date Range</Label>
              <Select value={dateRange} onValueChange={(val: any) => setDateRange(val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Channel</Label>
              <Select value={channelFilter} onValueChange={(val: any) => setChannelFilter(val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="email">Email Only</SelectItem>
                  <SelectItem value="sms">SMS Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Delivery Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{deliveryRate}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              {totals?.sent.toLocaleString()} of {totals?.queued.toLocaleString()} sent
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Confirmation Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{confirmationRate}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              {totals?.confirmed.toLocaleString()} confirmations
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Failed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">{totals?.failed || 0}</div>
            <div className="text-xs text-muted-foreground mt-1">
              After 3 retries
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <UserX className="h-4 w-4" />
              Opt-Outs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totals?.optOuts || 0}</div>
            <div className="text-xs text-muted-foreground mt-1">
              STOP commands
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status Distribution</CardTitle>
            <CardDescription>Breakdown of reminder outcomes</CardDescription>
          </CardHeader>
          <CardContent>
            {statusDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Channel Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Channel Performance</CardTitle>
            <CardDescription>Sent vs Failed by channel</CardDescription>
          </CardHeader>
          <CardContent>
            {channelBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={channelBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="channel" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="sent" fill={COLORS.sent} name="Sent" />
                  <Bar dataKey="failed" fill={COLORS.failed} name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional Metrics (SMS only) */}
      {channelFilter !== 'email' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Customer Engagement (SMS)</CardTitle>
            <CardDescription>Customer responses and interactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-500">{totals?.confirmed || 0}</div>
                <div className="text-sm text-muted-foreground">Confirmed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-500">{totals?.customerCanceled || 0}</div>
                <div className="text-sm text-muted-foreground">Canceled by Customer</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-500">{totals?.optOuts || 0}</div>
                <div className="text-sm text-muted-foreground">Opted Out</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
